import React, { useState } from "react";
import api from "../lib/api";
import { useLang } from "../contexts/LangContext";
import { useCart } from "../contexts/CartContext";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Sparkles, Save, Plus, ChefHat, Clock, Users } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { DIETS } from "../lib/diets";

export default function AIRicette() {
  const { t, lang } = useLang();
  const { addMany } = useCart();
  const { user } = useAuth();

  const [prompt, setPrompt] = useState("");
  const [diet, setDiet] = useState("");
  const [region, setRegion] = useState("");
  const [servings, setServings] = useState(2);
  const [recipe, setRecipe] = useState(null);
  const [loading, setLoading] = useState(false);

  const generate = async () => {
    if (!prompt.trim()) return toast.error("Scrivi cosa vuoi cucinare");
    setLoading(true);
    setRecipe(null);
    try {
      const r = await api.post("/recipes/generate", { prompt, diet, region, servings, lang });
      setRecipe(r.data);
    } catch (e) {
      toast.error("Errore generazione. Riprova.");
    } finally { setLoading(false); }
  };

  const saveRecipe = async () => {
    if (!user) return toast.error("Accedi per salvare la ricetta");
    try {
      await api.post(`/recipes/${recipe.id}/save`);
      toast.success("Ricetta salvata!");
    } catch { toast.error("Salvataggio fallito"); }
  };

  const addAll = () => {
    if (!recipe?.mapped_boxes_detail?.length) return toast.error("Nessun box mappato");
    addMany(recipe.mapped_boxes_detail.map(b => ({
      product_id: b.id, product_type: "box", name: b.name, image: b.image,
      unit_price: b.price, quantity: 1, unit: "pz"
    })));
    toast.success("Box aggiunte al carrello");
  };

  return (
    <div data-testid="ai-ricette-page" className="container-xl py-12 md:py-16">
      <div className="text-xs uppercase tracking-[0.3em] text-[hsl(var(--primary))] font-semibold mb-2 flex items-center gap-2">
        <Sparkles className="h-3.5 w-3.5" /> AI Powered
      </div>
      <h1 className="editorial-heading text-5xl md:text-6xl leading-none">{t("sections.ai", "Genera la tua ricetta con l'AI")}</h1>
      <p className="text-muted-foreground mt-3 max-w-2xl text-lg">{t("sections.ai_sub", "Dicci cosa ti va: l'AI crea la ricetta e mappa gli ingredienti alle box.")}</p>

      <div className="mt-10 grid lg:grid-cols-5 gap-8">
        <div className="lg:col-span-2 bg-card border border-border rounded-sm p-6 space-y-4 h-fit">
          <div>
            <label className="text-xs font-bold uppercase tracking-[0.2em]">Cosa ti va?</label>
            <Textarea
              data-testid="ai-prompt-input"
              value={prompt} onChange={e => setPrompt(e.target.value)} rows={4}
              placeholder={t("ai.placeholder", "Es: pasta veloce per una cena romantica...")}
              className="mt-2 bg-white border-border rounded-sm"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.2em]">Dieta</label>
              <Select value={diet || "any"} onValueChange={(v) => setDiet(v === "any" ? "" : v)}>
                <SelectTrigger data-testid="ai-diet-select" className="mt-2 bg-white border-border rounded-sm">
                  <SelectValue placeholder="Qualsiasi" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="any" data-testid="ai-diet-any">Qualsiasi</SelectItem>
                  {DIETS.map((d) => (
                    <SelectItem key={d.value} value={d.value} data-testid={`ai-diet-${d.value}`}>
                      {d.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.2em]">Regione</label>
              <Input data-testid="ai-region-input" value={region} onChange={e => setRegion(e.target.value)} placeholder="Es. Toscana" className="mt-2 bg-white rounded-sm" />
            </div>
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-[0.2em]">Persone</label>
            <Input data-testid="ai-servings-input" type="number" min="1" value={servings} onChange={e => setServings(parseInt(e.target.value) || 2)} className="mt-2 bg-white rounded-sm" />
          </div>
          <Button data-testid="ai-generate-btn" onClick={generate} disabled={loading} className="w-full bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--primary))]/90 rounded-sm gap-2">
            <Sparkles className="h-4 w-4" />
            {loading ? t("ai.generating", "Sto cucinando l'idea...") : t("ai.generate", "Genera ricetta")}
          </Button>
          <div className="text-xs text-muted-foreground">Powered by GPT-5.2</div>
        </div>

        <div className="lg:col-span-3 min-h-[400px]">
          {!recipe && !loading && (
            <div className="h-full flex flex-col items-center justify-center text-center p-12 border-2 border-dashed border-border rounded-sm">
              <ChefHat className="h-12 w-12 text-muted-foreground" />
              <div className="mt-4 text-lg editorial-heading">La tua ricetta apparirà qui</div>
              <div className="text-sm text-muted-foreground mt-1">Descrivi cosa ti va, l'AI fa il resto.</div>
            </div>
          )}
          {loading && (
            <div data-testid="ai-loading" className="p-8 border border-border rounded-sm bg-card">
              <div className="animate-pulse space-y-3">
                <div className="h-6 bg-muted rounded w-2/3" />
                <div className="h-4 bg-muted rounded w-1/2" />
                <div className="h-4 bg-muted rounded w-4/5" />
                <div className="h-4 bg-muted rounded w-3/5" />
              </div>
            </div>
          )}
          {recipe && (
            <div data-testid="ai-recipe-result" className="border border-border rounded-sm bg-card overflow-hidden scroll-fade-in">
              <div className="p-6 md:p-8">
                <div className="text-xs uppercase tracking-[0.3em] text-[hsl(var(--accent))] font-semibold">{recipe.region}</div>
                <h2 className="editorial-heading text-4xl md:text-5xl leading-[0.95] mt-1">{recipe.title}</h2>
                <p className="text-muted-foreground mt-2">{recipe.description}</p>
                <div className="flex flex-wrap gap-4 mt-4 text-sm">
                  <span className="chip bg-[hsl(var(--muted))]"><Clock className="h-3 w-3" />{recipe.prep_time} min</span>
                  <span className="chip bg-[hsl(var(--muted))]"><Users className="h-3 w-3" />{recipe.servings} persone</span>
                  <span className="chip bg-[hsl(var(--muted))]">{recipe.difficulty}</span>
                </div>
                <div className="mt-6 grid md:grid-cols-2 gap-6">
                  <div>
                    <div className="text-xs font-bold uppercase tracking-[0.2em] mb-3">Ingredienti</div>
                    <ul className="space-y-2">
                      {recipe.ingredients.map((ing, i) => (
                        <li key={i} data-testid={`ingredient-${i}`} className="flex items-start justify-between gap-3 text-sm border-b border-border pb-2">
                          <div>
                            <span className="font-medium">{ing.name}</span>
                            <span className="text-muted-foreground"> — {ing.quantity}</span>
                          </div>
                          {ing.mapped_box_id && (
                            <span className="chip bg-[hsl(var(--secondary))] text-white">in box</span>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <div className="text-xs font-bold uppercase tracking-[0.2em] mb-3">Preparazione</div>
                    <ol className="space-y-2 text-sm list-decimal ml-5">
                      {recipe.steps.map((s, i) => <li key={i}>{s}</li>)}
                    </ol>
                  </div>
                </div>
                {recipe.mapped_boxes_detail?.length > 0 && (
                  <div className="mt-6 bg-[hsl(var(--muted))] rounded-sm p-4">
                    <div className="text-xs font-bold uppercase tracking-[0.2em] mb-3">Box consigliate per questa ricetta</div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {recipe.mapped_boxes_detail.map(b => (
                        <Link key={b.id} to={`/box/${b.id}`} data-testid={`mapped-box-${b.id}`} className="flex items-center gap-3 bg-white rounded-sm p-2 hover:shadow-md transition-shadow">
                          <img src={b.image} className="w-14 h-14 object-cover rounded-sm" alt="" />
                          <div className="min-w-0 flex-1">
                            <div className="font-medium text-sm truncate">{b.name}</div>
                            <div className="text-xs text-[hsl(var(--primary))] font-bold">€{b.price.toFixed(2)}</div>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <div className="border-t border-border p-4 bg-[hsl(var(--muted))] flex flex-wrap gap-2 justify-end">
                <Button data-testid="ai-save-btn" variant="outline" onClick={saveRecipe} className="gap-2 rounded-sm">
                  <Save className="h-4 w-4" />{t("ai.save", "Salva ricetta")}
                </Button>
                <Button data-testid="ai-addall-btn" onClick={addAll} className="bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--primary))]/90 gap-2 rounded-sm">
                  <Plus className="h-4 w-4" />{t("ai.addall", "Aggiungi tutto al carrello")}
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
